import { setCredentials } from '../features/authSlice';
import { logger } from '../services/logger';

import { api } from './base';

import type { AppDispatch } from '../store';
import type {
	BulkUpdateFavoriteRequest,
	FavoritesResponse,
	GetUserFavoritesQuery,
	LegalAcceptanceResponse,
	MeResponse,
	SignupOnboardingStatus,
	UpdateFavoriteRequest,
	UserInfo,
} from './types';

export const userApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getMe: builder.query<MeResponse['data'], void>({
			query() {
				return {
					url: '/me',
					method: 'GET',
				};
			},
			transformResponse: (rawResult: MeResponse) => rawResult?.data,
			async onQueryStarted(args, { dispatch, queryFulfilled }) {
				try {
					const { data: user } = await queryFulfilled;
					logger.user({ email: user.email, id: user.id });
					dispatch(
						setCredentials({
							user,
							accessToken: undefined,
							refreshToken: undefined,
						}),
					);
				} catch (error) {
					// fail silently
				}
			},
			providesTags: ['Me'],
		}),
		getUserInfo: builder.query<UserInfo, void>({
			query() {
				return {
					url: '/users/info/',
					method: 'GET',
				};
			},
			transformResponse: (response: UserInfo) => response,
			providesTags: ['Me'],
		}),
		acceptLegalAgreement: builder.mutation<
			LegalAcceptanceResponse,
			// The version the client displayed to the user — the server records
			// it verbatim; its gate decides whether it satisfies the current
			// requirement. Not a literal type: the constant lives with the
			// agreement text in web-app, not here.
			{ version: string }
		>({
			query: (body) => ({
				url: '/users/legal-acceptance',
				method: 'POST',
				body,
			}),
			// Apply the acceptance to the `/me` cache from this mutation's
			// authoritative response rather than refetching. The caller navigates
			// immediately after, and a refetch racing that navigation serves a
			// pre-acceptance `/me` — which sends the user straight back to the
			// signup step they just cleared. Intentionally NOT `invalidatesTags`.
			async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
				try {
					const { data: response } = await queryFulfilled;
					dispatch(
						userApi.util.updateQueryData('getMe', undefined, (draft) => {
							if (!draft) return;
							draft.legal_acceptance = response.data;
							// The server says whether the recorded version satisfies
							// its current requirement; a stale client must keep the
							// gate up rather than assume it cleared.
							draft.legal_acceptance_required =
								response.legal_acceptance_required ?? false;
						}),
					);
				} catch {
					// Acceptance failed; the component surfaces the error.
				}
			},
		}),
		saveUserInfo: builder.mutation<
			{ message: string },
			{ name?: string; title?: string; enterprise_name?: string }
		>({
			query(body) {
				return {
					url: '/users/info/',
					method: 'POST',
					body,
				};
			},
			invalidatesTags: ['Me'],
			async onQueryStarted(arg, { queryFulfilled }) {
				try {
					await queryFulfilled;
				} catch (error) {
					throw new Error(`Failed to save user info`);
				}
			},
		}),
		getFavorites: builder.query<FavoritesResponse, GetUserFavoritesQuery>({
			query: (query) => ({
				url: '/users/favorites',
				method: 'GET',
				params: query,
			}),
			providesTags: ['Favorites'],
		}),
		updateFavorite: builder.mutation<FavoritesResponse, UpdateFavoriteRequest>({
			query: (body) => ({
				url: '/users/favorites',
				method: 'POST',
				body,
			}),
			invalidatesTags: ['Favorites'],
		}),
		bulkUpdateFavorites: builder.mutation<
			FavoritesResponse,
			BulkUpdateFavoriteRequest
		>({
			query: (body) => ({
				url: '/users/favorites/bulk',
				method: 'POST',
				body,
			}),
			invalidatesTags: ['Favorites'],
		}),
	}),
});

export const {
	useGetMeQuery,
	useGetUserInfoQuery,
	useAcceptLegalAgreementMutation,
	useSaveUserInfoMutation,
	useGetFavoritesQuery,
	useUpdateFavoriteMutation,
	useBulkUpdateFavoritesMutation,
} = userApi;

/**
 * Reflect a just-completed signup onboarding directly in the `/me` cache from
 * the completion mutation's authoritative response, instead of re-reading
 * `/me`. On preprod/prod the identity façade can briefly serve a
 * pre-completion `/me`; refetching it (via `invalidatesTags` or an explicit
 * `refetch`) repopulates `onboarding_completed: false` and sends
 * `AuthenticatedRoute`'s onboarding gate back to the trial-tasks screen — the
 * "Continue takes two clicks" bug. The mutation response is the source of
 * truth, so patch the cache and skip the round-trip. Patches both the RTK
 * query cache (which `useMe` prefers) and the mirrored `authState.user`.
 */
export const applyCompletedSignupOnboarding =
	(status: SignupOnboardingStatus) => (dispatch: AppDispatch) => {
		dispatch(
			userApi.util.updateQueryData('getMe', undefined, (draft) => {
				if (!draft) return;
				draft.onboarding_completed = true;
				draft.onboarding_status = status;
			}),
		);
		dispatch(
			setCredentials({
				user: { onboarding_completed: true, onboarding_status: status },
			}),
		);
	};
